import React from 'react';

import {TextField} from "@mui/material";
import {IsoDatePicker} from "../../components/IsoDatePicker";
import {IsoDateRangePicker} from "../../components/IsoDateRangePicker";
import {CampaignStatusSelect} from "../../components/MaterialSelects/CampaignStatusSelect";
import {ClientCompanySelect} from "../../components/MaterialSelects/ClientCompanySelect";
import {ClientSubTypeSelect} from "../../components/MaterialSelects/ClientSubTypeSelect";
import {RegionSelectGrouped} from "../../components/MaterialSelects/RegionSelectGrouped";
import {PosterSizeSelect} from "../../components/MaterialSelects/PosterSizeSelect";
import {DistributorSelect} from "../../components/MaterialSelects/DistributorSelect";
import SearchForm from "../../components/search-form";
import {SalesTeamSelect} from "../../components/MaterialSelects/SalesTeamSelect";
import {UserSelect} from "../../components/MaterialSelects/UserSelect";
import CurrencyRangeInput from "../../components/currency-range-input";
import {useUserContext} from "../../contexts/user-context";
import { CurrencyField } from '../../components/CurrencyInput';
import { ClientCompanyContactSelect } from '../../components/MaterialSelects/ClientCompanyContactSelect';
import {Select} from "../../components/MaterialSelects/Select";


// The closed set of campaign types. Values match the DB `type` column; labels are the
// human-readable names shown in search and the editor.
export const CAMPAIGN_TYPES = [
  {id: 'standard', label: 'Standard'},
  {id: 'creative_studio', label: 'Creative Studio'}
];


const CampaignSearch = (props) => {
  const user = useUserContext();
  const typeFieldDef = {
    label: 'Campaign Type', id: 'type',
    variants: [
      {
        id: 'in',
        label: 'Lookup',
        multi: true,
        editor: ({ input }) => <Select {...input} multiple allowBlank options={CAMPAIGN_TYPES} labelField="label" label="Campaign Type"/>
      }
    ]
  };

  const fieldTypes = [
    {
      label: 'Job Reference', id: 'job_reference',
      default: true,
      variants: [
        {
          default: true,
          label: 'Like',
          id: 'like',
          editor: ({ input }) => <TextField className="slim" label="Job Reference" placeholder="Job Ref" {...input}/>
        },
      ]
    },
    {
      label: 'Campaign name', id: 'name',
      variants: [{
        id: 'like',
        label: 'Like',
        editor: ({ input }) => <TextField className="slim" label="Campaign Name" placeholder="Name of Campaign" {...input}/>
      }
      ]
    },
    {
      label: 'Regional AM Revenue Allocation', id: 'regional_am_revenue_id',
      variants: [{
        id: 'in',
        label: 'Lookup',
        multi: true,
        clearOnChange: true,
        getValue: (val) => {
          return val.id;
        },
        editor: ({ input }) => <RegionSelectGrouped valueIsId={true} multiple={true} {...input} />
      }]
    },
    {
      label: 'Invoice Number', id: 'invoice',
      variants: [{
        id: 'exact',
        label: 'Exact',
        editor: ({ input }) => <TextField className="slim" label="Invoice Number" placeholder="Invoice Number" {...input}/>
      }
      ]
    },
    {
      label: 'Inter Company Sales Job Ref', id: 'sales_job_ref',
      variants: [{
        id: 'like',
        label: 'Like',
        editor: ({ input }) => <TextField className="slim" label="Sales Job Ref" placeholder="Sales Job Ref" {...input}/>
      }
      ]
    },
    {
      label: 'Purchase Order', id: 'purchase_order',
      variants: [{
        id: 'exact',
        label: 'Exact',
        editor: ({ input }) => <TextField className="slim" label="Purchase Order" placeholder="Purchase Order" {...input} />
      }
      ]
    },
    {
      label: 'Sales Team', id: 'sales_team',
      variants: [{
        id: 'in',
        label: 'Lookup',
        multi: true,
        clearOnChange: true,
        getValue: (val) => {
          return val.id;
        },
        editor: ({ input }) => <SalesTeamSelect valueIsId={true} multiple={true} {...input} />
      }]

    }, {
      label: 'Sales Contact', id: 'our_contact',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          multi: true,
          clearOnChange: true,
          getValue: (val) => {
            return val.id;
          },
          editor: ({ input }) => <UserSelect {...input} label="User" valueIsId={true} multiple={true} />
        }
      ]
    }, {
      label: "Completion Date", id: 'completion_date',
      variants: [
        {
          id: 'range',
          label: 'Range',
          editor: ({ input }) => <IsoDateRangePicker {...input} label="Date Range"/>
        },
        {
          id: 'exact',
          label: 'Exact',
          editor: ({ input }) => <IsoDatePicker {...input} label="Date"/>
        }

      ]
    }, {
      label: "Start Date", id: 'start_date',
      variants: [
        {
          label: 'Range',
          id: 'range',
          editor: ({ input }) => <IsoDateRangePicker label="Date Range" {...input}/>
        },
        {
          label: 'Exact',
          id: 'exact',
          editor: ({ input }) => <IsoDatePicker {...input} label="Date"/>
        }

      ]
    }, {
      label: "Artwork Received Date", id: 'artwork_received_date',
      variants: [
        {
          label: 'Range',
          id: 'range',
          editor: ({ input }) => <IsoDateRangePicker {...input}  label="Date"/>
        },
        {
          id: 'exact',
          label: 'Exact',
          editor: ({ input }) => <IsoDatePicker {...input} label="Date" />
        },
        {
          id: 'blank',
          label: 'Blank',
          getValue: () => 1,
          editor: ({input}) => <div {...input} />
        }
      ]
    }, {
      label: "Status", id: 'status',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          multi: true,
          editor: ({ input }) => <CampaignStatusSelect multiple={true} {...input} label="Status" />
        }
      ]
    }, {
      label: "Total Price", id: 'total_price',
      variants: [
        {
          label: 'Range',
          id: 'range',
          type:'integer',
          editor: ({ input }) => <CurrencyRangeInput {...input} />
        }, {
          label: 'Exact',
          id: 'exact',
          editor: ({ input }) => <CurrencyField {...input} label="Exact" fill={false}/>
        }, {
          label: 'No VAT Range',
          id: 'range_no_vat',
          editor: ({ input }) => <CurrencyRangeInput {...input} />
        }, {
          label: 'No VAT Exact',
          id: 'exact_no_vat',
          editor: ({ input }) => <CurrencyField {...input} label="Exact, no VAT" fill={false}/>
        }
      ]
    }, {
      label: "Client", id: 'client',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          getValue: (val) => {
            return val.id;
          },
          multi: true,
          editor: ({ input }) => <ClientCompanySelect valueIsId={true} multiple={true} {...input} />
        }, {
          id: 'like',
          label: 'Like',
          editor: ({ input }) => <TextField label="Client" {...input} />
        }
      ]
    }, {
      label: "Contact", id: 'contact',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          multi: true,
          editor: ({ input }) => <ClientCompanyContactSelect {...input} valueIsId={true} multiple={true} label="Contact"/>
        }, {
          id: 'like',
          label: 'Like',
          editor: ({ input }) => <TextField label="Contact" {...input} />
        }
      ]
    }, {
      label: "Campaign Client Type", id: 'client_sub_type',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          getValue: (val) => val.id,
          multi: true,
          editor: ({ input }) => <ClientSubTypeSelect valueIsId={true} multiple={true} {...input} popup={true} />
        }
      ]
    }, {
      label: "Area Booking Region", id: 'area_booking_region',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          getValue: (val) => val.id,
          multi: true,
          editor: ({ input }) => <RegionSelectGrouped {...input} areaOnly={true} valueIsId={true} multiple={true} popup={true} label="Region"/>
        }
      ]
    }, {
      label: "Area Booking Poster Size", id: 'area_booking_poster_size',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          getValue: (val) => val.id,
          multi: true,
          editor: ({ input }) => <PosterSizeSelect isStreetPoster={true} valueIsId={true} multiple={true} {...input} label="Poster Size"/>
        }
      ]
    }, {
      label: "Area Booking Distributor", id: 'area_booking_distributor',
      variants: [
        {
          id: 'in',
          label: 'Lookup',
          getValue: (val) => val.id,
          multi: true,
          editor: ({ input }) => <DistributorSelect valueIsId={true} multiple={true} {...input} label="Area Booking Distributor"/>
        }
      ]
    }, {
      label: "Area Booking Date Up", id: 'area_booking_date_up',
      variants: [
        {
          id: 'range',
          label: 'Range',
          editor: ({ input }) => <IsoDateRangePicker {...input} label="Date"/>
        },
        {
          id: 'exact',
          label: 'Exact',
          editor: ({ input }) => <IsoDatePicker {...input} label="Date"/>
        }
      ]
    }, {
      label: "Area Booking Date Down", id: 'area_booking_date_down',
      variants: [
        {
          id: 'range',
          label: 'Range',
          editor: ({ input }) => <IsoDateRangePicker {...input}  label="Date"/>
        },
        {
          id: 'exact',
          label: 'Exact',
          editor: ({ input }) => <IsoDatePicker {...input} label="Date"/>
        }
      ]
    }, typeFieldDef
  ];
  if (!user.hasPermission('campaigns.am_revenue')) {
    delete(fieldTypes[2]);
  }
  // On /cs-campaigns the creative-studio restriction is a visible, pre-selected field instead
  // of a hidden fixed variable (design D2/D3). The default row only applies on a fresh visit —
  // URL params and saved searches take precedence in SearchForm's init chain.
  const defaultFields = props.isCreativeStudio ? [
    {
      column: typeFieldDef,
      variant: typeFieldDef.variants[0],
      value: [CAMPAIGN_TYPES.find((t) => t.id === 'creative_studio')]
    }
  ] : undefined;
  return <SearchForm onCancel={props.onCancel} onSearch={props.onSearch} isOpen={props.isOpen}
    params={props.params}
    defaultFields={defaultFields}
    fieldTypes={fieldTypes}
    name="campaigns"
    label="Campaigns "
  />
}

/*CampaignSearch.propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  params: PropTypes.array,
  onSearch: PropTypes.func.isRequired
};*/
export default CampaignSearch;
